import { SingleSelectSetting, SettingKeys } from 'src/types/settings';
import { useUniqueId, useUserSettings } from 'src/hooks';
import { useCallback, useMemo, useRef } from 'react';
import { SVG_SIZE_M } from 'src/constants';
import {
  DrawerTooltipContainer,
  DrawerTooltipContainerRef,
} from 'src/v2/commonComponents/DrawerTooltipContainer';
import styles from './SettingsSelect.module.scss';
import { SelectorButton } from 'src/v2/components/LeftSidePanel/components/LLMSelectorSection/components/SelectorButton';
import { TaskSkill } from 'src/types';
import { Icon } from 'src/components/Icon';
import { ExternalModelItem } from 'src/v2/components/OtherModelsSelector/components/TextModelList/components/ExternalModelItem';
import {
  getModelDisplayName,
  getModelIcon,
  prepareModelsList,
} from 'src/utils';
import { useUserMetadata, useUserSettingsUpdate } from 'src/hooks';

interface SettingsSelectProps {
  settingKey: SettingKeys;
  skill?: TaskSkill;
  value?: string;
  onChange?: (value: string) => void;
}

// if we pass onChange, it will be called instead of updateUserSetting
export const SettingsSelect = ({
  settingKey,
  skill,
  onChange,
  value,
}: SettingsSelectProps) => {
  const drawerRef = useRef<DrawerTooltipContainerRef>(null);

  const { setting } = useUserSettings<SingleSelectSetting>(settingKey);

  const { updateUserSetting } = useUserSettingsUpdate();

  const { metadata } = useUserMetadata();
  const { uniqueId } = useUniqueId();

  const tooltipAnchorId = useMemo(
    () => `settings-${skill}-tooltip-${uniqueId}`,
    [skill, uniqueId],
  );

  const handleSelectClick = useCallback(
    (id: string) => {
      if (!onChange) {
        updateUserSetting({ value: id, settingKey });
      } else {
        onChange(id);
      }

      drawerRef.current?.onVisibilityToggle();
    },
    [onChange, updateUserSetting, settingKey],
  );

  const modelItems = useMemo(() => {
    return prepareModelsList(setting?.options || {}, metadata)
      .filter((item) => {
        // Filter Ninja models for Research option
        if (settingKey === SettingKeys.RESEARCH_PRIMARY_MODEL) {
          if (
            item.id.includes('ninjatech') &&
            item.id !== 'agent/ninjatech/text-auto'
          )
            return false;
          return true;
        } else {
          return true;
        }
      })
      .map((item) => {
        return {
          id: item.id,
          icon: item.iconName ? (
            <Icon type={item.iconName} size={SVG_SIZE_M} />
          ) : (
            <></>
          ),
          title: item.title,
          selected: item.id === (value || setting?.value),
          availableIn: item.availableIn,
          onClick: () => handleSelectClick(item.id),
        };
      });
  }, [
    setting?.options,
    setting?.value,
    value,
    handleSelectClick,
    metadata,
    settingKey,
  ]);

  return (
    <DrawerTooltipContainer
      ref={drawerRef}
      id={tooltipAnchorId}
      place="bottom"
      offset={8}
      classNameTooltip={styles.tooltip}
      ToggleButton={SelectorButton}
      toggleButtonProps={{
        label: getModelDisplayName({
          targetModel: value || setting?.value,
          metadata: metadata,
        }),
        icon: getModelIcon({
          targetModel: value || setting?.value,
          metadata: metadata,
        }),
      }}
      disabledToggle={!setting?.user_editable}
    >
      <div
        className={styles.container}
        data-e2e={`model-settings-${settingKey}`}
      >
        {modelItems.map((item) => (
          <ExternalModelItem key={item.id} {...item} />
        ))}
      </div>
    </DrawerTooltipContainer>
  );
};
